import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { SearchAddon } from '@xterm/addon-search'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { Icon, Input } from 'semantic-ui-react'
import '@xterm/xterm/css/xterm.css'
import './terminal-output.scss'

interface Props {
  content: string
  height?: string
  className?: string
  initialScrollToTop?: boolean
  fillHeight?: boolean
  /** When true, new content is appended instead of replacing */
  streaming?: boolean
  /** Connection status indicator for streaming mode */
  isConnected?: boolean
  /** Shows a loading indicator when true */
  isLoading?: boolean
}

const TerminalOutput = ({
  content,
  height = '400px',
  className = '',
  initialScrollToTop = false,
  fillHeight = false,
  streaming = false,
  isConnected = false,
  isLoading = false
}: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const terminalRef = useRef<Terminal | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const searchAddonRef = useRef<SearchAddon | null>(null)
  const lastContentRef = useRef<string>('')
  const rafIdsRef = useRef<number[]>([])
  const timeoutIdsRef = useRef<number[]>([])

  const [searchVisible, setSearchVisible] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResultCount, setSearchResultCount] = useState<number | null>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Helper to safely schedule RAF and track it for cleanup
  const scheduleRaf = useCallback((callback: () => void): number => {
    const id = requestAnimationFrame(() => {
      // Remove this ID from tracking
      rafIdsRef.current = rafIdsRef.current.filter(rafId => rafId !== id)
      callback()
    })
    rafIdsRef.current.push(id)
    return id
  }, [])

  // Helper to cancel all pending RAF callbacks
  const cancelAllRafs = useCallback((): void => {
    rafIdsRef.current.forEach(id => { cancelAnimationFrame(id) })
    rafIdsRef.current = []
  }, [])

  // Helper to safely schedule setTimeout and track it for cleanup
  const scheduleTimeout = useCallback((callback: () => void, delay: number): number => {
    const id = window.setTimeout(() => {
      // Remove this ID from tracking
      timeoutIdsRef.current = timeoutIdsRef.current.filter(timeoutId => timeoutId !== id)
      callback()
    }, delay)
    timeoutIdsRef.current.push(id)
    return id
  }, [])

  // Helper to cancel all pending timeouts
  const cancelAllTimeouts = useCallback((): void => {
    timeoutIdsRef.current.forEach(id => { clearTimeout(id) })
    timeoutIdsRef.current = []
  }, [])

  // Safe fit function that checks terminal validity
  const safeFit = useCallback((): void => {
    const terminal = terminalRef.current
    const fitAddon = fitAddonRef.current
    const container = containerRef.current

    if (terminal === null || fitAddon === null || container === null) {
      return
    }

    // Check if container has dimensions before attempting to fit
    const rect = container.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) {
      console.debug('Terminal container has no dimensions, skipping fit')
      return
    }

    // Check if container is visible (not display:none or visibility:hidden)
    const computedStyle = window.getComputedStyle(container)
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
      console.debug('Terminal container is not visible, skipping fit')
      return
    }

    // Check if terminal's internal state is ready
    // The terminal must have cols and rows initialized (xterm internals)
    try {
      // Access the terminal's buffer to verify it's initialized
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const term = terminal as any
      if (typeof term.cols !== 'number' || typeof term.rows !== 'number' || term._core?.viewport == null) {
        console.debug('Terminal internal state not ready, skipping fit')
        return
      }
    } catch (e) {
      console.debug('Terminal state check failed, skipping fit')
      return
    }

    try {
      fitAddon.fit()
    } catch (error) {
      // Silently catch fit errors if terminal is being disposed
      console.debug('Terminal fit error (likely during cleanup):', error)
    }
  }, [])

  // Memoize terminal options to prevent recreation
  const terminalOptions = useMemo(() => ({
    cursorBlink: false,
    cursorStyle: 'bar' as const,
    disableStdin: true,
    convertEol: true,
    scrollback: 10000,
    fontSize: 13,
    fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', 'Monaco', monospace",
    theme: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#d4d4d4',
      cursorAccent: '#1e1e1e',
      selectionBackground: '#264f78',
      black: '#000000',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      white: '#e5e5e5',
      brightBlack: '#666666',
      brightRed: '#f14c4c',
      brightGreen: '#23d18b',
      brightYellow: '#f5f543',
      brightBlue: '#3b8eea',
      brightMagenta: '#d670d6',
      brightCyan: '#29b8db',
      brightWhite: '#ffffff'
    }
  }), [])

  // Initialize terminal once on mount
  useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    // Check if already initialized
    if (terminalRef.current !== null) return

    const terminal = new Terminal(terminalOptions)
    const fitAddon = new FitAddon()
    const searchAddon = new SearchAddon()
    const webLinksAddon = new WebLinksAddon()

    terminal.loadAddon(fitAddon)
    terminal.loadAddon(searchAddon)
    terminal.loadAddon(webLinksAddon)

    terminal.open(container)

    terminalRef.current = terminal
    fitAddonRef.current = fitAddon
    searchAddonRef.current = searchAddon

    // Initial fit after opening - use timeout + RAF to ensure xterm is fully initialized
    // This gives xterm time to set up its internal viewport and buffer state
    const timeoutId = window.setTimeout(() => {
      const rafId = requestAnimationFrame(() => {
        const term = terminalRef.current
        const fit = fitAddonRef.current
        const cont = containerRef.current
        if (term !== null && fit !== null && cont !== null) {
          // Additional check: ensure container has dimensions
          const rect = cont.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            // Use safeFit which has additional internal state checks
            try {
              // Check terminal internal state before fitting
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const termInternal = term as any
              if (typeof termInternal.cols === 'number' && typeof termInternal.rows === 'number' && termInternal._core?.viewport != null) {
                fit.fit()
              } else {
                console.debug('Terminal not ready for initial fit, will retry on resize')
              }
            } catch (error) {
              console.debug('Initial terminal fit error:', error)
            }
          }
        }
      })
      rafIdsRef.current.push(rafId)
    }, 50)
    timeoutIdsRef.current.push(timeoutId)

    // Cleanup on unmount
    return () => {
      // Cancel the initial fit timeout and RAF if they haven't executed yet
      clearTimeout(timeoutId)
      // Cancel any other pending RAF callbacks and timeouts
      cancelAllRafs()
      cancelAllTimeouts()
      // Dispose terminal
      const term = terminalRef.current
      if (term !== null) {
        term.dispose()
      }
      terminalRef.current = null
      fitAddonRef.current = null
      searchAddonRef.current = null
    }
  }, [terminalOptions, cancelAllRafs, cancelAllTimeouts])

  // Write content to terminal when it changes
  useEffect(() => {
    const terminal = terminalRef.current
    if (terminal === null || content.length === 0) return

    // Skip if content hasn't actually changed
    if (lastContentRef.current === content) return

    if (streaming) {
      // In streaming mode, append only new content
      const previousContent = lastContentRef.current
      lastContentRef.current = content

      if (content.startsWith(previousContent)) {
        // Content was appended - just write the new part
        const newContent = content.slice(previousContent.length)
        if (newContent.length > 0) {
          terminal.write(newContent)
          // Auto-scroll to bottom in streaming mode (no fit needed for append)
          scheduleRaf(() => {
            const term = terminalRef.current
            if (term !== null) {
              term.scrollToBottom()
            }
          })
        }
      } else {
        // Content was replaced - reset and write all
        terminal.reset()
        terminal.write(content)
        scheduleRaf(() => {
          const term = terminalRef.current
          if (term !== null) {
            term.scrollToBottom()
          }
        })
      }
    } else {
      // Standard mode - replace all content
      lastContentRef.current = content
      terminal.reset()
      // Add blank line at the end to prevent last line from being clipped
      terminal.write(content + '\n')

      // Fit after content is written, then scroll as needed
      scheduleRaf(() => {
        safeFit()
        const term = terminalRef.current
        if (term !== null) {
          if (initialScrollToTop) {
            term.scrollToTop()
          }
        }
      })
    }
  }, [content, initialScrollToTop, streaming, scheduleRaf, safeFit])

  // Fit terminal when streaming mode changes or on initial mount
  useEffect(() => {
    // Always fit when the component first mounts or when switching modes
    // to ensure the terminal has the correct dimensions
    scheduleTimeout(() => {
      safeFit()
    }, 100)
  }, [streaming, safeFit, scheduleTimeout])

  // Handle window resize
  useEffect(() => {
    const handleResize = (): void => {
      // In streaming mode, skip automatic resize to prevent terminal from growing
      // The terminal should maintain its fixed height and use scrolling
      if (!streaming) {
        safeFit()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => { window.removeEventListener('resize', handleResize) }
  }, [safeFit, streaming])

  // Handle container resize and visibility changes using ResizeObserver
  useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    // Use ResizeObserver to detect when container size changes or becomes visible
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Check if the container has valid dimensions
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          // Skip fitting in streaming mode to prevent terminal from growing
          // The terminal should maintain a fixed height and use scrolling instead
          if (!streaming) {
            // Use RAF to avoid excessive fits during rapid resize
            scheduleRaf(() => {
              safeFit()
            })
          }
        }
      }
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [safeFit, scheduleRaf, streaming])

  const performSearch = useCallback((query: string, direction: 'next' | 'previous' = 'next'): void => {
    const searchAddon = searchAddonRef.current
    if (searchAddon === null || query.length === 0) {
      setSearchResultCount(null)
      return
    }

    const found = direction === 'next'
      ? searchAddon.findNext(query, { caseSensitive: false, incremental: true })
      : searchAddon.findPrevious(query, { caseSensitive: false })

    setSearchResultCount(found ? 1 : 0)
  }, [])

  const handleSearchChange = useCallback((value: string): void => {
    setSearchQuery(value)
    if (value.length > 0) {
      performSearch(value)
    } else {
      searchAddonRef.current?.clearDecorations()
      setSearchResultCount(null)
    }
  }, [performSearch])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) {
        performSearch(searchQuery, 'previous')
      } else {
        performSearch(searchQuery, 'next')
      }
    }
    if (e.key === 'Escape') {
      setSearchVisible(false)
      setSearchQuery('')
      searchAddonRef.current?.clearDecorations()
    }
  }, [performSearch, searchQuery])

  const scrollToTop = useCallback((): void => {
    terminalRef.current?.scrollToTop()
  }, [])

  const scrollToBottom = useCallback((): void => {
    terminalRef.current?.scrollToBottom()
  }, [])

  const toggleSearch = useCallback((): void => {
    setSearchVisible(prev => {
      if (prev) {
        setSearchQuery('')
        searchAddonRef.current?.clearDecorations()
        return false
      } else {
        scheduleTimeout(() => { searchInputRef.current?.focus() }, 0)
        return true
      }
    })
  }, [scheduleTimeout])

  const closeSearch = useCallback((): void => {
    setSearchVisible(false)
    setSearchQuery('')
    searchAddonRef.current?.clearDecorations()
  }, [])

  // Attach keyboard shortcuts directly to the terminal
  // Note: attachCustomKeyEventHandler returns void, handler is removed when terminal is disposed
  useEffect(() => {
    const terminal = terminalRef.current
    if (terminal === null) return

    terminal.attachCustomKeyEventHandler((e: KeyboardEvent) => {
      // Only handle keydown events
      if (e.type !== 'keydown') return true

      // '?' to show keyboard shortcuts
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault()
        setShowShortcuts(prev => !prev)
        return false
      }
      // '/' to open search
      if (e.key === '/' && !e.shiftKey) {
        e.preventDefault()
        setSearchVisible(true)
        setShowShortcuts(false)
        scheduleTimeout(() => { searchInputRef.current?.focus() }, 0)
        return false
      }
      // Home key to scroll to top
      if (e.key === 'Home') {
        e.preventDefault()
        terminal.scrollToTop()
        return false
      }
      // End key to scroll to bottom
      if (e.key === 'End') {
        e.preventDefault()
        terminal.scrollToBottom()
        return false
      }
      // Page Up to scroll up
      if (e.key === 'PageUp') {
        e.preventDefault()
        terminal.scrollPages(-1)
        return false
      }
      // Page Down to scroll down
      if (e.key === 'PageDown') {
        e.preventDefault()
        terminal.scrollPages(1)
        return false
      }
      // Arrow Up to scroll up one line
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        terminal.scrollLines(-1)
        return false
      }
      // Arrow Down to scroll down one line
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        terminal.scrollLines(1)
        return false
      }
      // 'f' to toggle fullscreen
      if (e.key === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        setIsFullscreen(prev => !prev)
        scheduleTimeout(() => { safeFit() }, 100)
        return false
      }
      // Escape to close search, shortcuts, or exit fullscreen
      if (e.key === 'Escape') {
        if (showShortcuts) {
          setShowShortcuts(false)
          return false
        }
        if (searchVisible) {
          setSearchVisible(false)
          setSearchQuery('')
          searchAddonRef.current?.clearDecorations()
          return false
        }
        if (isFullscreen) {
          setIsFullscreen(false)
          scheduleTimeout(() => { safeFit() }, 100)
          return false
        }
      }
      return true // Let xterm handle other keys
    })
  }, [searchVisible, showShortcuts, isFullscreen, safeFit, scheduleTimeout])

  // Keyboard shortcuts (on wrapper div as fallback)
  const handleKeyDown = useCallback((e: React.KeyboardEvent): void => {
    // '/' to open search
    if (e.key === '/' && !e.shiftKey) {
      e.preventDefault()
      setSearchVisible(true)
      scheduleTimeout(() => { searchInputRef.current?.focus() }, 0)
    }
    if (e.key === 'Escape' && searchVisible) {
      closeSearch()
    }
    if (e.key === 'Home') {
      e.preventDefault()
      terminalRef.current?.scrollToTop()
    }
    if (e.key === 'End') {
      e.preventDefault()
      terminalRef.current?.scrollToBottom()
    }
  }, [searchVisible, closeSearch, scheduleTimeout])

  const toggleShortcuts = useCallback((): void => {
    setShowShortcuts(prev => !prev)
  }, [])

  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(prev => !prev)
    // Refit terminal after fullscreen toggle
    scheduleTimeout(() => {
      safeFit()
    }, 100)
  }, [safeFit, scheduleTimeout])

  // Refit terminal when fullscreen changes
  useEffect(() => {
    safeFit()
  }, [isFullscreen, safeFit])

  return (
    <div className={`terminal-output-container ${fillHeight ? 'fill-height' : ''} ${isFullscreen ? 'fullscreen' : ''} ${className}`} onKeyDown={handleKeyDown}>
      {showShortcuts && (
        <div className="keyboard-help-overlay" onClick={() => { setShowShortcuts(false) }}>
          <div className="keyboard-help-modal" onClick={(e) => { e.stopPropagation() }}>
            <h2>Keyboard Shortcuts</h2>
            <div className="keyboard-shortcuts">
              <h3>Terminal</h3>
              <div className="shortcut-row">
                <kbd>?</kbd>
                <span>Toggle this menu</span>
              </div>
              <div className="shortcut-row">
                <kbd>/</kbd>
                <span>Search</span>
              </div>
              <div className="shortcut-row">
                <kbd>Home</kbd>
                <span>Scroll to top</span>
              </div>
              <div className="shortcut-row">
                <kbd>End</kbd>
                <span>Scroll to bottom</span>
              </div>
              <div className="shortcut-row">
                <kbd>&uarr;</kbd>
                <span>Scroll up one line</span>
              </div>
              <div className="shortcut-row">
                <kbd>&darr;</kbd>
                <span>Scroll down one line</span>
              </div>
              <div className="shortcut-row">
                <kbd>PgUp</kbd>
                <span>Scroll up one page</span>
              </div>
              <div className="shortcut-row">
                <kbd>PgDn</kbd>
                <span>Scroll down one page</span>
              </div>
              <div className="shortcut-row">
                <kbd>f</kbd>
                <span>Toggle fullscreen</span>
              </div>
              <div className="shortcut-row">
                <kbd>Esc</kbd>
                <span>Close search/menu/fullscreen</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="terminal-toolbar">
        <div className="toolbar-left">
          {streaming && (
            <div className={`streaming-status ${isConnected ? 'connected' : isLoading ? 'connecting' : 'disconnected'}`}>
              <Icon name={isConnected ? 'circle' : isLoading ? 'circle notch' : 'circle outline'} loading={isLoading} />
              <span>{isConnected ? 'Live' : isLoading ? 'Connecting...' : 'Disconnected'}</span>
            </div>
          )}
          {searchVisible && (
            <div className="search-box">
              <Input
                ref={searchInputRef as any}
                size="mini"
                placeholder="Search..."
                value={searchQuery}
                onChange={(_, data) => { handleSearchChange(data.value) }}
                onKeyDown={handleSearchKeyDown}
                icon={
                  <Icon
                    name="search"
                    link
                    onClick={() => { performSearch(searchQuery) }}
                  />
                }
              />
              {searchResultCount !== null && (
                <span className="search-result-count">
                  {searchResultCount > 0 ? 'Match found' : 'No matches'}
                </span>
              )}
              <Icon
                name="arrow up"
                link
                title="Previous (Shift+Enter)"
                onClick={() => { performSearch(searchQuery, 'previous') }}
              />
              <Icon
                name="arrow down"
                link
                title="Next (Enter)"
                onClick={() => { performSearch(searchQuery, 'next') }}
              />
              <Icon
                name="close"
                link
                onClick={closeSearch}
              />
            </div>
          )}
        </div>
        <div className="toolbar-right">
          <Icon
            name="question circle outline"
            link
            title="Keyboard shortcuts (?)"
            onClick={toggleShortcuts}
            className={showShortcuts ? 'active' : ''}
          />
          <Icon
            name="search"
            link
            title="Search (/)"
            onClick={toggleSearch}
            className={searchVisible ? 'active' : ''}
          />
          <Icon
            name="arrow up"
            link
            title="Scroll to top (Home)"
            onClick={scrollToTop}
          />
          <Icon
            name="arrow down"
            link
            title="Scroll to bottom (End)"
            onClick={scrollToBottom}
          />
          <Icon
            name={isFullscreen ? 'compress' : 'expand'}
            link
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen (f)'}
            onClick={toggleFullscreen}
            className={isFullscreen ? 'active' : ''}
          />
        </div>
      </div>
      <div
        className={`terminal-wrapper ${fillHeight ? 'fill-height' : ''}`}
        style={fillHeight ? undefined : { height }}
        tabIndex={0}
      >
        <div ref={containerRef} className="terminal-content" />
      </div>
    </div>
  )
}

export default TerminalOutput
